import { pipe } from 'fp-ts/function';
import { array, boolean, string } from 'fp-ts';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Card, Title, Stack, Container } from '@optimus-web/core';
import {
    CheckboxValues,
    CheckInFieldsTooltipValues,
    CheckInFieldsValues,
    ConfigurationEditableColumnValues,
} from '../types';
import { driverLicenseSection, FieldData, identityCardSection, passportSection } from '../sections';
import { CheckInFieldsTable } from '../table/CheckInFieldsTable';
import { ChangeCheckboxesState, SetVisibilityData } from '../table/types';
import { TabItemValue } from '../CheckInTabs/enums';

interface DocumentSectionTableProps {
    initialValues: CheckInFieldsValues;
    isLegallyRequiredRecord: Record<CheckInConfigurationType, boolean>;
    editableValues: ConfigurationEditableColumnValues;
    setVisibilityData: SetVisibilityData;
    selectedDocumentType: CheckInConfigurationType;
    visibilityData: CheckInFieldsValues;
    tooltipValues: CheckInFieldsTooltipValues;
    activeTabValue: TabItemValue;
    changeCheckboxesState: ChangeCheckboxesState;
    checkboxValues: CheckboxValues;
    enhancedCheckInFormDocumentsSectionEnabled: boolean;
}

interface GetFieldsTableOptions {
    title: Mews.TextKey;
    documentTextKey: Mews.TextKey;
    section: FieldData[];
    supportedDocumentTypes: CheckInConfigurationType[];
}

const passportData = {
    title: TextKey.Passport,
    documentTextKey: TextKey.PassportNumber,
    section: passportSection,
    supportedDocumentTypes: [
        CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
        CheckInConfigurationType.PassportOrIdentityCard,
        CheckInConfigurationType.DirectPassport,
    ],
};

const identityCardData = {
    title: TextKey.IdCard,
    documentTextKey: TextKey.IdentityNumber,
    section: identityCardSection,
    supportedDocumentTypes: [
        CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
        CheckInConfigurationType.PassportOrIdentityCard,
        CheckInConfigurationType.DirectIdentityCard,
    ],
};

const driverLicenseData = {
    title: TextKey.DriversLicense,
    documentTextKey: TextKey.LicenseNumber,
    section: driverLicenseSection,
    supportedDocumentTypes: [
        CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense,
        CheckInConfigurationType.DirectDriversLicense,
    ],
};

export const DocumentSectionTables = ({
    initialValues,
    editableValues,
    isLegallyRequiredRecord,
    setVisibilityData,
    visibilityData,
    selectedDocumentType,
    tooltipValues,
    changeCheckboxesState,
    checkboxValues,
    activeTabValue,
    enhancedCheckInFormDocumentsSectionEnabled,
}: DocumentSectionTableProps) => {
    /* Field data shown in the UI based on selectedDocumentType */
    const documentsFieldData = [passportData, identityCardData, driverLicenseData]
        .filter(({ supportedDocumentTypes }) => supportedDocumentTypes.includes(selectedDocumentType))
        .map(({ section }) => section)
        .flat();

    const updateAndSyncDocumentVisibility: SetVisibilityData = ({ type, value, columnName }) => {
        setVisibilityData({ type, value, columnName });

        if (value === CheckInConfigurationValue.Default) {
            return;
        }

        const parentType = selectedDocumentType;
        const userTypes = ['reservationOwner', 'reservationAdult', 'reservationChild'] as const;

        userTypes.forEach((userType) => {
            const prevParent = visibilityData[parentType][userType];

            const shouldSync =
                // If the child is set to Required, update parent if is not already set to Required.
                (value === CheckInConfigurationValue.Required && prevParent !== CheckInConfigurationValue.Required) ||
                // If the child is set to Optional, only update the parent if it is currently set to Default.
                (value === CheckInConfigurationValue.Optional && prevParent === CheckInConfigurationValue.Default);

            if (shouldSync) {
                setVisibilityData({
                    type: parentType,
                    value,
                    columnName: userType,
                });
            }
        });
    };

    const getFieldsTable = ({ title, documentTextKey, section, supportedDocumentTypes }: GetFieldsTableOptions) =>
        pipe(
            supportedDocumentTypes,
            array.elem(string.Eq)(selectedDocumentType),
            boolean.fold(
                () => null,
                () => (
                    <Stack key={title} spacing="100" vertical>
                        <Title level={2}>
                            <LocalizedText textKey={title} />
                        </Title>
                        <Card padding="100">
                            <Container padding={['200', '0']} />
                            <CheckInFieldsTable
                                initialValues={initialValues}
                                tooltipValues={tooltipValues}
                                isLegallyRequiredRecord={isLegallyRequiredRecord}
                                editableValues={editableValues}
                                section={[
                                    /**
                                     * The first subsection field should align with the setting of the main document section.
                                     * Essentially, we utilize a specific text key to 'simulate' the field as belonging to a particular document,
                                     * but in reality, it corresponds to the one in the main table.
                                     */
                                    {
                                        isDisabled: true,
                                        type: selectedDocumentType,
                                        textKey: documentTextKey,
                                    },
                                    ...section,
                                ]}
                                activeTabValue={activeTabValue}
                                setVisibilityData={
                                    enhancedCheckInFormDocumentsSectionEnabled
                                        ? updateAndSyncDocumentVisibility
                                        : setVisibilityData
                                }
                                visibilityData={visibilityData}
                                changeCheckboxesState={(props) =>
                                    changeCheckboxesState({ ...props, documentsFieldData })
                                }
                                checkboxValues={checkboxValues}
                            />
                        </Card>
                    </Stack>
                )
            )
        );

    const documentsComponent = [passportData, identityCardData, driverLicenseData].map(getFieldsTable);

    return <>{documentsComponent}</>;
};
